import { useInfiniteQuery } from '@tanstack/react-query';
import { formatDate } from '@ui';
import { getCycles } from '../../services/cycles';
import { PastPortfolio, PortfolioOrders } from '../../types/baskets';
import { CamelCasedObject } from '../../types/camelCaseObject';
import { Cycle } from '../../types/cycles';

// Number of items to load per page
const PAGE_SIZE = 20;

const usePastBaskets = (fromTime?: number) => {
  const {
    data,
    isLoading: isPastBasketsLoading,
    isSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<CamelCasedObject<Cycle>[]>({
    queryKey: ['past-cycles'],
    queryFn: ({ pageParam = 1 }) =>
      getCycles({
        status: 'completed',
        order_by: '-modified',
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has less items than the page size, we've reached the end
      return lastPage.length < PAGE_SIZE ? undefined : allPages.length + 1;
    },
    refetchOnWindowFocus: true,
  });

  // Transform and flatten all pages of data
  const dataSource: Array<PastPortfolio> = [];
  if (isSuccess && data) {
    data.pages.forEach((pastBaskets) => {
      pastBaskets.forEach((cycle) => {
        const created = new Date(cycle.created * 1000);
        const date = formatDate(created);

        const portfolioDetails: PastPortfolio = {
          date: date ? date : '',
          type: cycle.strategy == 'hunter_long' ? 'long' : 'short',
          current: cycle.currentValue,
          invested: cycle.investedAmount,
          duration: cycle?.endedAt ? cycle?.endedAt - cycle.created : 0,
          totalReturns: cycle.returns,
          orders: [],
        };

        cycle.orders.forEach((order) => {
          const orderDetails: PortfolioOrders = {
            ticker: order.security,
            invested:
              cycle.strategy === 'hunter_long'
                ? order.initialAmount
                : order.endAmount,
            current:
              cycle.strategy === 'hunter_long'
                ? order.endAmount
                : order.initialAmount,
          };
          portfolioDetails.orders && portfolioDetails.orders.push(orderDetails);
        });
        dataSource.push(portfolioDetails);
      });
    });
  }

  return {
    isPastBasketsLoading,
    dataSource,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export default usePastBaskets;
